import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, Inject, PLATFORM_ID, Output, EventEmitter, NgZone, Optional, Host, Injector } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Subject, takeUntil, firstValueFrom } from 'rxjs';
import { PreviewService } from '../../../services/preview.service';
import { ChatService } from '../../../services/chat.service';
import { PreviewData } from '../../../interfaces/api.interface';
import { WebsiteFileService } from '../../../services/website-file.service';
import { ChatPhase } from '../../../interfaces/ai-content.interface';
import { IndexedDBService } from '../../../services/indexeddb.service';
import { InlineContentEditorComponent, ContentEditRequest } from '../inline-content-editor/inline-content-editor.component';
import { ImageUploadRequest } from '../inline-image-uploader/inline-image-uploader.component';
import { COLOR_THEMES } from '../../../models/color-theme.model';

@Component({
  selector: 'app-website-preview',
  imports: [CommonModule],
  templateUrl: './website-preview.component.html',
  styleUrls: ['./website-preview.component.scss']
})
export class WebsitePreviewComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('previewFrame') previewFrame!: ElementRef<HTMLIFrameElement>;
  
  private destroy$ = new Subject<void>();
  
  selectedTemplate: number = 1;
  selectedTheme: string = 'default';
  isLoading: boolean = false; // Add loading state
  
  // Deployment-related properties
  currentPhase: ChatPhase = ChatPhase.DATA_COLLECTION;
  isDeploying: boolean = false;
  isLogoUploadCompleted: boolean = false;
  showColorPalette: boolean = false;
  
  // Save progress state
  isSavingProgress: boolean = false;
  
  // Edit mode state
  isEditMode: boolean = false;
  hasAIContent: boolean = false;
  
  // Inline content editing
  @Output() openInlineEditor = new EventEmitter<ContentEditRequest>();
  
  // Inline image uploading
  @Output() imageUploaderRequested = new EventEmitter<ImageUploadRequest>();
  
  // Theme picker properties
  colorThemes = COLOR_THEMES;
  showThemePicker: boolean = false;
  
  // Company GUID will be obtained from chat service (from query parameter)
  
  private getCompanyGuid(): string {
    return this.chatService.getCompanyGuid();
  }
  
  ChatPhase = ChatPhase;

  constructor(
    private previewService: PreviewService,
    private chatService: ChatService,
    private websiteFileService: WebsiteFileService,
    private indexedDBService: IndexedDBService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private ngZone: NgZone,
    private injector: Injector
  ) { }

  ngOnInit(): void {
    // Subscribe to template changes
    this.chatService.selectedTemplate$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async template => {
        if (template !== this.selectedTemplate) {
          this.selectedTemplate = template;
          // Show loader during template initialization
          this.isLoading = true;
          // Initialize file system with new template
          await this.previewService.initializePreview(template);
        }
      });

    // Subscribe to theme changes
    this.chatService.selectedColorTheme$
      .pipe(takeUntil(this.destroy$))
      .subscribe(theme => {
        this.selectedTheme = theme;
      });

    // Subscribe to preview updates
    this.previewService.previewUpdate$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        console.log('WebsitePreviewComponent: Received preview update', {
          hasData: !!data,
          template: data?.template,
          theme: data?.theme,
          companyName: data?.supplierData?.companyName,
          htmlLength: data?.templateHtml?.length
        });
        if (data) {
          this.updatePreview(data);
        }
        // Update hasAIContent when preview updates (in case AI content is applied)
        this.hasAIContent = this.previewService.hasAIContentBeenGenerated();
      });

    // Subscribe to chat service states for deployment button visibility
    this.chatService.currentPhase$
      .pipe(takeUntil(this.destroy$))
      .subscribe(phase => {
        this.currentPhase = phase;
        // Update phase-dependent properties
        this.isLogoUploadCompleted = phase === ChatPhase.DEPLOYMENT || phase === ChatPhase.COMPLETED;
        // Color palette is no longer phase-dependent - theme picker is always available in header
      });

    this.chatService.isDeploying$
      .pipe(takeUntil(this.destroy$))
      .subscribe(deploying => {
        this.isDeploying = deploying;
      });

    // Subscribe to edit mode changes
    this.previewService.isEditMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(editMode => {
        this.isEditMode = editMode;
        console.log('WebsitePreviewComponent: Edit mode changed to:', editMode);
      });

    // Check if AI content has been generated
    this.hasAIContent = this.previewService.hasAIContentBeenGenerated();

    // Note: isLogoUploadCompleted and showColorPalette are handled through currentPhase subscription
  }

  ngAfterViewInit(): void {
    // Set up global function for iframe navigation (only in browser)
    if (isPlatformBrowser(this.platformId)) {
      (window as any).loadTemplatePage = (pageName: string) => {
        this.chatService.loadTemplatePage(pageName);
      };
      
      // Set up message listener for iframe communication
      window.addEventListener('message', (event) => {
        console.log('WebsitePreview: Received message from iframe:', event.data);
        if (event.data && event.data.type === 'openContentEditor') {
          const { tagName, currentContent, elementRect } = event.data;
          console.log('WebsitePreview: Processing openContentEditor message');
          this.ngZone.run(() => {
            this.openContentEditor(tagName, currentContent, elementRect);
          });
        } else if (event.data && event.data.type === 'openImageUploader') {
          const { tagName, currentImageSrc, elementRect } = event.data;
          console.log('WebsitePreview: Processing openImageUploader message');
          console.log('WebsitePreview: Image uploader emitter exists:', !!this.imageUploaderRequested);
          this.ngZone.run(() => {
            this.openImageUploaderHandler(tagName, currentImageSrc, elementRect);
          });
        }
      });
      
      // Set up click outside handler for theme picker
      document.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        const themePickerContainer = target.closest('.theme-picker-container');
        if (!themePickerContainer && this.showThemePicker) {
          this.ngZone.run(() => {
            this.showThemePicker = false;
          });
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updatePreview(data: PreviewData): void {
    console.log('WebsitePreviewComponent.updatePreview: Starting...');
    if (!this.previewFrame) {
      console.log('WebsitePreviewComponent.updatePreview: No preview frame available');
      return;
    }

    // Start loading
    this.isLoading = true;

    console.log('WebsitePreviewComponent.updatePreview: Generating HTML...');
    const html = this.previewService.generatePreviewHtml(data);
    console.log('WebsitePreviewComponent.updatePreview: Generated HTML length:', html.length);
    console.log('WebsitePreviewComponent.updatePreview: Setting iframe srcdoc...');
    this.previewFrame.nativeElement.srcdoc = html;
    console.log('WebsitePreviewComponent.updatePreview: Complete');
  }

  onIframeLoad(): void {
    // Hide loader when iframe loads
    this.isLoading = false;
    console.log('Preview iframe loaded');
    
    // Trigger blur removal after iframe loads if AI content exists
    if (this.previewService.hasAIContentBeenGenerated()) {
      setTimeout(() => {
        this.previewService.triggerBlurRemoval();
      }, 500);
    }
  }

  /**
   * Check if all questions are completed to enable deployment
   */
  allQuestionsCompleted(): boolean {
    return this.chatService.isDataCollectionComplete();
  }

  /**
   * Check if deploy button should be visible (always visible)
   */
  shouldShowDeployButton(): boolean {
    return true;
  }

  /**
   * Check if deploy button should be enabled
   */
  isDeployButtonEnabled(): boolean {
    return this.currentPhase === ChatPhase.DEPLOYMENT && !this.isDeploying;
  }

  /**
   * Deploy the website by calling the chat service
   */
  async deployWebsite(): Promise<void> {
    // Switch to browse mode before deployment to show clean preview
    if (this.isEditMode) {
      await this.previewService.setEditMode(false, this.getCompanyGuid());
      console.log('🔄 Switched to browse mode for deployment');
    }
    
    // Remove blur effects from all placeholders before deployment
    this.previewService.removeBlurEffectsForDeployment();
    console.log('🎯 Removed blur effects from all placeholders for deployment');
    
    await this.chatService.deployWebsite();
  }



  /**
   * Toggle edit mode on/off
   */
  async toggleEditMode(): Promise<void> {
    await this.previewService.toggleEditMode(this.getCompanyGuid());
    // Refresh preview to apply/remove edit functionality
    await this.refreshPreview();
  }

  /**
   * Refresh the current preview
   */
  async refreshPreview(): Promise<void> {
    // Refresh the current preview
    const currentData = this.chatService.getSupplierData();
    this.previewService.updatePreview(currentData, this.selectedTemplate, this.selectedTheme, this.getCompanyGuid());
    
    // Debug info
    console.log(`Preview refreshed: ${this.websiteFileService.getFileCount()} files loaded`);
  }

  /**
   * Open the inline content editor
   */
  openContentEditor(tagName: string, currentContent: string, elementRect: DOMRect): void {
    console.log('WebsitePreview: Opening content editor for tag:', tagName, 'Content:', currentContent);
    
    const editRequest = {
      tagName,
      currentContent,
      elementPosition: {
        x: elementRect.left,
        y: elementRect.top,
        width: elementRect.width,
        height: elementRect.height
      }
    };
    
    // Try event emission first
    this.openInlineEditor.emit(editRequest);
    console.log('🔥 WebsitePreview: Content Editor event emitted for tag:', tagName);
    console.log('🔍 WebsitePreview: Content Editor observers count:', this.openInlineEditor.observers.length);
    
    // FALLBACK: If no observers, use direct method call
    if (this.openInlineEditor.observers.length === 0) {
      console.log('🔄 WebsitePreview: No observers, using direct parent method call...');
      const parentComponent = (window as any).builderComponent;
      if (parentComponent && typeof parentComponent.openInlineEditor === 'function') {
        console.log('🔄 WebsitePreview: Calling openInlineEditor directly');
        parentComponent.openInlineEditor(editRequest);
      } else {
        console.log('❌ WebsitePreview: Parent component not accessible');
      }
    }
  }

  /**
   * Open the inline image uploader
   */
  openImageUploaderHandler(tagName: string, currentImageSrc: string, elementRect: DOMRect): void {
    console.log('WebsitePreview: Opening image uploader for tag:', tagName, 'Image:', currentImageSrc);
    console.log('WebsitePreview: Element rect:', elementRect);
    
    const uploadRequest = {
      tagName,
      currentImageSrc,
      elementPosition: {
        x: elementRect.left,
        y: elementRect.top,
        width: elementRect.width,
        height: elementRect.height
      }
    };
    
    console.log('WebsitePreview: About to emit openImageUploader event with:', uploadRequest);
    
    // Try event emission first
    this.imageUploaderRequested.emit(uploadRequest);
    console.log('🔥 WebsitePreview: Image Uploader event emitted for tag:', tagName);
    console.log('🔍 WebsitePreview: Event emitter observers count:', this.imageUploaderRequested.observers.length);
    
    // FALLBACK: If no observers, use direct method call
    if (this.imageUploaderRequested.observers.length === 0) {
      console.log('🔄 WebsitePreview: No observers, using direct parent method call...');
      const parentComponent = (window as any).builderComponent;
      if (parentComponent && typeof parentComponent.handleImageUploader === 'function') {
        console.log('🔄 WebsitePreview: Calling handleImageUploader directly');
        parentComponent.handleImageUploader(uploadRequest);
      } else {
        console.log('❌ WebsitePreview: Parent component not accessible');
      }
    }
  }

  /**
   * Get current supplier data for the editor
   */
  getCurrentSupplierData(): any {
    return this.chatService.getSupplierData();
  }

  /**
   * Get current semantic content for the editor
   */
  getCurrentSemanticContent(): { [tagName: string]: string } {
    return this.previewService.getCurrentSemanticContent();
  }

  /**
   * Handle content update from the editor
   */
  async onContentUpdated(update: { tagName: string; newContent: string }): Promise<void> {
    // Update the content in the preview
    await this.previewService.updateContentInPreview(update.tagName, update.newContent, this.getCompanyGuid());
    
    // Close the editor
    // this.isEditMode = false; // Removed
    // this.currentEditRequest = null; // Removed
  }

  /**
   * Handle editor cancellation
   */
  onEditCancelled(): void {
    // this.isEditMode = false; // Removed
    // this.currentEditRequest = null; // Removed
  }

  /**
   * Handle keep old content action
   */
  onKeepOldContent(): void {
    // this.isEditMode = false; // Removed
    // this.currentEditRequest = null; // Removed
  }

  /**
   * Save user progress to IndexedDB
   */
  async saveProgress(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      console.warn('Save progress is only available in browser environment');
      return;
    }

    this.isSavingProgress = true;

    try {
      console.log('💾 Starting to save user progress...');

      // 1. Save supplier data
      const supplierData = this.chatService.getSupplierData();
      await this.indexedDBService.setUserSupplierData(this.getCompanyGuid(), supplierData);
      console.log('✅ Supplier data saved');

      // 2. Save selected theme
      await this.indexedDBService.setUserTheme(this.getCompanyGuid(), this.selectedTheme);
      console.log('✅ Theme preference saved');

      // 3. Save selected template
      await this.indexedDBService.storeMetadata('selectedTemplate', this.selectedTemplate);
      console.log('✅ Template selection saved');

      // 4. Save current chat messages
      const currentMessages = await firstValueFrom(this.chatService.messages$);
      
      // Clear existing messages for this user first
      await this.indexedDBService.clearChatMessages(this.getCompanyGuid());
      
      // Save all current messages
      for (const message of currentMessages) {
        await this.indexedDBService.storeChatMessage(this.getCompanyGuid(), message);
      }
      console.log('✅ Chat messages saved');

      // 5. Save additional progress metadata
      const progressMetadata = {
        currentPhase: this.currentPhase,
        isDataCollectionComplete: this.chatService.isDataCollectionComplete(),
        savedAt: new Date().toISOString(),
        companyGuid: this.chatService.getCompanyGuid()
      };
      
      await this.indexedDBService.storeMetadata(`userProgress_${this.getCompanyGuid()}`, progressMetadata);
      console.log('✅ Progress metadata saved');

      // Show success message to user
      await this.chatService.addMessage('✅ Your progress has been saved successfully! You can safely close the browser and return later to continue where you left off.');

      console.log('🎉 All progress saved successfully');

    } catch (error) {
      console.error('❌ Failed to save progress:', error);
      
      // Show error message to user
      await this.chatService.addMessage('❌ Sorry, there was an error saving your progress. Please try again.');
      
    } finally {
      this.isSavingProgress = false;
    }
  }

  /**
   * Toggle theme picker dropdown
   */
  toggleThemePicker(): void {
    this.showThemePicker = !this.showThemePicker;
  }

  /**
   * Select a theme and apply it
   */
  async selectTheme(themeKey: string): Promise<void> {
    // Update the selected theme
    this.chatService.selectColorTheme(themeKey);
    
    // Save theme preference to IndexedDB
    if (isPlatformBrowser(this.platformId)) {
      await this.indexedDBService.setUserTheme(this.getCompanyGuid(), themeKey);
    }
    
    // Hide the theme picker
    this.showThemePicker = false;
    
    console.log('✅ Theme changed to:', themeKey);
  }
}
